import { useState, useEffect } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { QuestionnaireForm } from '@/features/assessments/forms/questionnaire-form'
import { QuestionnaireSummary } from '@/features/assessments/components/questionnaire-summary'
import { LoadingSpinner } from '@/components/ui/loading-spinner'
import { api } from '@/lib/api-client'
import { Assessment } from '@/features/assessments/types'
import { useToast } from '@/hooks/use-toast'
import { useAuthorization } from '@/features/authorization/AuthorizationContext'

export function QuestionnairePage() {
  const { id } = useParams()
  const [assessment, setAssessment] = useState<Assessment | null>(null)

  // Debug: Log the ID from URL params
  console.log('QuestionnairePage - ID from URL params:', id)
  const [answers, setAnswers] = useState<Record<string, number>>({})
  const [isLoading, setIsLoading] = useState(true)
  const [showQuestionnaire, setShowQuestionnaire] = useState(false)
  const [readOnlyMode, setReadOnlyMode] = useState(false)
  const [searchParams] = useSearchParams()
  const { hasAction } = useAuthorization()
  const canManage = assessment?.is_creator || hasAction('manage_assessments')
  const isReadOnly = searchParams.get('readonly') === 'true' || readOnlyMode || (assessment !== null && !canManage)
  const { toast } = useToast()

  // Auto-show questionnaire form for in-progress or read-only
  useEffect(() => {
    if (isReadOnly) {
      setShowQuestionnaire(true)
    }
    if (assessment && assessment.status === 'in_progress') {
      setShowQuestionnaire(true)
    }
  }, [isReadOnly, assessment])

  useEffect(() => {
    if (id) {
      loadAssessment()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]) // loadAssessment is stable, no need to include in deps

  // Reload answers when returning from questionnaire
  useEffect(() => {
    if (!showQuestionnaire && id) {
      // Reload answers when summary is shown
      const reloadAnswers = async () => {
        try {
          const answersResponse = await api.assessments.answers(id!)
          const answerMap: Record<string, number> = {}
          const answersData = answersResponse.data?.items || answersResponse.data?.data || answersResponse.data || []
          if (Array.isArray(answersData)) {
            answersData.forEach((answer: any) => {
              const questionId = answer.question_id || answer.questionId
              const value = answer.value ?? answer.score  // fallback to score
              if (questionId && value !== null && value !== undefined) {
                answerMap[questionId] = value
              }
            })
          }
          console.log('Reloaded answers:', Object.keys(answerMap).length, 'answers')
          setAnswers(answerMap)
        } catch (error) {
          console.error('Failed to reload answers:', error)
        }
      }
      reloadAnswers()
    }
  }, [showQuestionnaire, id])

  const loadAssessment = async () => {
    try {
      setIsLoading(true)
      const response = await api.assessments.get(id!)
      // Handle the response structure - backend returns {data: {...}}
      const assessmentData = response.data?.data || response.data
      console.log('Assessment data from API:', assessmentData)
      console.log('Assessment ID from data:', assessmentData?.id)
      console.log('Full response data:', response.data)

      // Ensure the assessment has the correct ID from URL params
      if (assessmentData && !assessmentData.id) {
        assessmentData.id = id!
        console.log('Set assessment ID from URL params:', assessmentData.id)
      }

      setAssessment(assessmentData)

      // Load answers
      const answersResponse = await api.assessments.answers(id!)
      const answerMap: Record<string, number> = {}

      // Handle the response structure - backend returns {data: [...]}
      const answersData = answersResponse.data?.items || answersResponse.data?.data || answersResponse.data || []
      if (Array.isArray(answersData)) {
        answersData.forEach((answer: any) => {
          // API returns question_id (snake_case), not questionId
          const questionId = answer.question_id || answer.questionId
          const value = answer.value ?? answer.score  // fallback to score
          if (questionId && value !== null && value !== undefined) {
            answerMap[questionId] = value
          }
        })
      }
      console.log('Loaded answers:', Object.keys(answerMap).length, 'answers')
      setAnswers(answerMap)
    } catch (error) {
      console.error('Failed to load assessment:', error)
      toast({
        title: "Error",
        description: "Failed to load assessment. Please try again.",
        variant: "destructive",
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleStartQuestionnaire = () => {
    setShowQuestionnaire(true)
  }

  const handleComplete = () => {
    setShowQuestionnaire(false)
    loadAssessment() // Reload to get updated status
  }

  const handleViewResults = () => {
    window.location.href = `/assessments/${id}/results`
  }

  const handleViewAnswers = () => {
    setReadOnlyMode(true)
    setShowQuestionnaire(true)
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    )
  }

  if (!assessment) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-gray-900">Assessment not found</h2>
        <p className="text-gray-500 mt-2">The requested assessment could not be found.</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="md:flex md:items-center md:justify-between">
        <div className="min-w-0 flex-1">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:truncate sm:text-3xl sm:tracking-tight">
            {isReadOnly ? 'Assessment Answers' : 'IT Maturity Questionnaire'}
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Assessment: {assessment.name}
          </p>
        </div>
      </div>

      {showQuestionnaire ? (
        <QuestionnaireForm
          assessmentId={assessment.id}
          assessment={assessment}
          onComplete={handleComplete}
          readOnly={isReadOnly}
        />
      ) : (
        <QuestionnaireSummary
          assessment={assessment}
          answers={answers}
          onStartQuestionnaire={handleStartQuestionnaire}
          onViewResults={handleViewResults}
          onViewAnswers={handleViewAnswers}
        />
      )}
    </div>
  )
}
